import { useCallback, useMemo, useState } from 'react'
import { ReviewStepCard } from '@/features/bookkeeping/review-step'
import { useNavigate, useParams } from 'react-router-dom'
import { Badge, CardColors, HeroCard, Separator, Text } from '@/ui'
import { Alert } from '@/base'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons'
import { ImageBackgroundColors, TextColors, TypographyStyles } from '@/styles'
import { useReviewItems } from './hooks'
import {
  userSubmitBookkeepingModule,
  FETCH_USER_SUBMIT_BOOKKEEPING_MODULE_KEY,
  FETCH_USER_GET_BOOKKEEPING_STEP_KEY,
  FETCH_USER_UPDATE_BOOKKEEPING_STEP_CONTEXT_KEY,
} from '@/features/bookkeeping/end-of-year-review/actions'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { getFetchError, selectIsFetchingForKeys } from '@/reducers/fetch'
import { NavButtons } from '@/features/bookkeeping/end-of-year-review/nav-buttons'
import { PageHeader } from '@/components/PageHeader'

const steps = {
  followUp: {
    name: 'Review "Uncategorized" transactions',
    route: 'follow-up-transactions',
  },
  statements: {
    name: 'Upload missing statements',
    route: 'follow-up-statements',
  },
  yearEnd: {
    name: 'Upload year-end documents',
    route: 'follow-up-documents',
  },
}
export const ReviewAndCloseBooks = () => {
  const { year } = useParams()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [showErrorState, setShowErrorState] = useState(false)

  const setReviewIndex = (route: string) => {
    navigate(`/taxes/annual/review-and-close-books/${year}/${route}`)
  }

  const {
    showPendingBankStatements,
    showPendingTransaction,
    showPendingDocuments,
    isPendingBankStatements,
    isPendingTransaction,
    isPendingDocuments,
  } = useReviewItems()

  const reviewSteps = useMemo(() => {
    return [
      ...(showPendingBankStatements
        ? [
            {
              title: 'Connected Accounts',
              step: steps.statements,
              key: 'follow-up-statements',
              isPending: isPendingBankStatements,
            },
          ]
        : []),
      ...(showPendingTransaction
        ? [
            {
              title: 'Close your books',
              step: steps.followUp,
              pendingText:
                'Add notes to transactions where the bookkeeping team needs more information.',
              key: 'follow-up-transactions',
              isPending: isPendingTransaction,
            },
          ]
        : []),
      ...(showPendingDocuments
        ? [
            {
              title: 'Year-End Documents',
              step: steps.yearEnd,
              pendingText:
                'Re-upload documents that the bookkeeping team has flagged.',
              key: 'follow-up-documents',
              isPending: isPendingDocuments,
            },
          ]
        : []),
    ]
  }, [
    showPendingBankStatements,
    isPendingBankStatements,
    showPendingTransaction,
    isPendingTransaction,
    showPendingDocuments,
    isPendingDocuments,
  ])

  const isReadyToSubmit = useMemo(() => {
    return reviewSteps.every(({ isPending }) => !isPending)
  }, [reviewSteps])

  const goToTaxesAnnual = useCallback(() => {
    navigate('/taxes/annual')
  }, [navigate])

  const isLoading = useReselector(selectIsFetchingForKeys, [
    FETCH_USER_SUBMIT_BOOKKEEPING_MODULE_KEY,
    FETCH_USER_GET_BOOKKEEPING_STEP_KEY,
    FETCH_USER_UPDATE_BOOKKEEPING_STEP_CONTEXT_KEY,
  ])

  const submitError = useReselector(
    getFetchError,
    FETCH_USER_SUBMIT_BOOKKEEPING_MODULE_KEY
  )

  const submitAndNavigate = useCallback(async () => {
    if (isReadyToSubmit) {
      const result = await dispatch(userSubmitBookkeepingModule())
      if (result) {
        goToTaxesAnnual()
      }
    } else {
      setShowErrorState(true)
    }
  }, [dispatch, goToTaxesAnnual, isReadyToSubmit])

  return (
    <div className="flex flex-col max-w-[920px] m-auto self-center">
      <div className="flex justify-between items-center">
        <PageHeader
          header={`${year} Year-End Bookkeeping`}
          extraContent={
            <Badge variant="secondary" color="green">
              Autosaved
            </Badge>
          }
        />
      </div>
      {showErrorState && (
        <div className="mt-4">
          <Alert
            type="error"
            title="Complete all steps to send updates."
            customIcon={
              <FontAwesomeIcon
                icon={faCircleExclamation}
                style={{ width: 20, height: 20 }}
              />
            }
          >
            Use the &ldquo;Go to Step&rdquo; button to complete all sections
            before sending updates to your bookkeeper.
          </Alert>
        </div>
      )}
      {submitError && (
        <div className="mt-4">
          <Alert
            type="error"
            title="Error submitting books"
            customIcon={
              <FontAwesomeIcon
                icon={faCircleExclamation}
                style={{ width: 20, height: 20 }}
              />
            }
          >
            {submitError.message}
          </Alert>
        </div>
      )}

      <div className="max-w-[972px] self-center">
        <HeroCard
          imageSrc="https://heard-images.s3.amazonaws.com/assets/ledger-questions.svg"
          imageAlt="ledger questions illustration"
          imageWidth={180}
          color={CardColors.LEVEL_0_NO_BORDER}
          title="Needs follow-up"
          imageColor={ImageBackgroundColors.YELLOW}
          description="The bookkeeping team has marked these items as needing additional follow-up from you. Please complete them ASAP so we can complete your end-of-year bookkeeping soon."
          className="max-w-2xl"
        >
          <div className="flex flex-col gap-6 mt-6 w-full">
            {reviewSteps.map(({ title, step, pendingText, key, isPending }) => (
              <div key={key}>
                <Text
                  textAlign="center"
                  variant={TypographyStyles.EYEBROW}
                  color={TextColors.MUTED}
                >
                  {title}
                </Text>
                <ReviewStepCard
                  key={key}
                  step={step}
                  setReviewIndex={setReviewIndex}
                  pendingText={pendingText}
                  showErrorState={showErrorState && isPending}
                  isPending={isPending}
                />
              </div>
            ))}
          </div>
        </HeroCard>
      </div>
      <Separator style={{ margin: 0 }} />

      <NavButtons
        isStepComplete
        stepBack={goToTaxesAnnual}
        stepForward={submitAndNavigate}
        saveAndExit={goToTaxesAnnual}
        isLoading={isLoading}
        forwardText="Send Updates"
      />
    </div>
  )
}
